import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaSearch,
  FaEdit,
  FaKey,
  FaTrash,
  FaCheck,
  FaTimes,
  FaEye,
  FaChevronLeft,
  FaChevronRight,
  FaUserShield,
  FaUserCheck,
  FaUserTimes,
  FaPlus,
  FaImage,
  FaSignInAlt,
  FaCreditCard
} from 'react-icons/fa';
import AdminAPI from '../../utils/adminApi';
import config from '../../config';

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    username: ''
  });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        ...(searchTerm && { search: searchTerm })
      });

      const data = await AdminAPI.get(`/api/manage/users?${params}`);
      setUsers(data.users);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      showMessage('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;

    try {
      await AdminAPI.post(`/api/manage/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });

      showMessage(`Password reset successfully for ${selectedUser.email}`);
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to reset password:', err);
      showMessage('Failed to reset password', 'error');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await AdminAPI.delete(`/api/manage/users/${selectedUser.id}`);

      showMessage(`User ${selectedUser.email} has been deleted`);
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Failed to delete user:', err);
      showMessage('Failed to delete user', 'error');
    }
  };

  const toggleUserStatus = async (user, field) => {
    try {
      const newValue = !user[field];
      await AdminAPI.put(`/api/manage/users/${user.id}`, {
        [field]: newValue
      });

      // Update local state
      setUsers(users.map(u =>
        u.id === user.id ? { ...u, [field]: newValue } : u
      ));

      showMessage(`User ${field} updated successfully`);
    } catch (err) {
      console.error(`Failed to update user ${field}:`, err);
      showMessage(`Failed to update user ${field}`, 'error');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) return;

    try {
      await AdminAPI.post('/api/manage/users', newUser);

      showMessage(`User ${newUser.email} created successfully`);
      setShowCreateModal(false);
      setNewUser({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        username: ''
      });
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Failed to create user:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to create user';
      showMessage(errorMessage, 'error');
    }
  };

  const handleLogoUpload = async () => {
    if (!selectedFile || !selectedUser) return;

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const token = localStorage.getItem('admin_access_token');
      const response = await fetch(`${config.API_URL}/api/manage/users/${selectedUser.id}/upload-logo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload logo');
      }

      showMessage(`Logo uploaded for ${selectedUser.email}`);
      setShowLogoModal(false);
      setSelectedFile(null);
      setSelectedUser(null);
      fetchUsers(); // Refresh the list
    } catch (err) {
      console.error('Failed to upload logo:', err);
      showMessage(err.message || 'Failed to upload logo', 'error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleImpersonate = async (user) => {
    try {
      const data = await AdminAPI.post(`/api/admin/impersonate/${user.id}`);
      // Save admin tokens so we can restore later
      localStorage.setItem('admin_impersonate_backup', JSON.stringify({
        access_token: localStorage.getItem('admin_access_token'),
        refresh_token: localStorage.getItem('admin_refresh_token'),
      }));
      // Store impersonated user tokens
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('token_type', 'bearer');
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('impersonating', JSON.stringify({
        email: user.email,
        username: user.username,
        name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
      }));
      window.open('/creator-dashboard', '_blank');
      showMessage(`Impersonating ${user.email} in new tab`);
    } catch (err) {
      console.error('Failed to impersonate user:', err);
      showMessage('Failed to impersonate user', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getBadgeColor = (user) => {
    if (user.is_superuser) return 'bg-red-500';
    if (!user.is_active) return 'bg-gray-500';
    if (!user.is_verified) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getBadgeText = (user) => {
    if (user.is_superuser) return 'Admin';
    if (!user.is_active) return 'Inactive';
    if (!user.is_verified) return 'Unverified';
    return 'Active';
  };

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/manage')}
              className="p-2 text-gray-400 hover:text-white transition"
            >
              <FaArrowLeft className="text-xl" />
            </button>
            <div>
              <h1 className="text-4xl font-bold">User Management</h1>
              <p className="text-gray-400">Manage user accounts, passwords, and permissions</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition"
          >
            <FaPlus />
            <span>Create User</span>
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            messageType === 'error'
              ? 'bg-red-500/20 border border-red-500/50 text-red-400'
              : 'bg-green-500/20 border border-green-500/50 text-green-400'
          }`}>
            {message}
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={handleSearch}
              className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">User</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Created</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {loading ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-400">
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center text-gray-400">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-800/50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium">{user.first_name} {user.last_name}</div>
                          <div className="text-sm text-gray-400">{user.email}</div>
                          {user.username && (
                            <div className="text-sm text-gray-500">@{user.username}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getBadgeColor(user)} text-white`}>
                            {getBadgeText(user)}
                          </span>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => toggleUserStatus(user, 'is_active')}
                              className={`text-xs px-2 py-1 rounded ${
                                user.is_active
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                  : 'bg-gray-500/20 text-gray-400 hover:bg-gray-500/30'
                              }`}
                            >
                              {user.is_active ? <FaUserCheck /> : <FaUserTimes />}
                            </button>
                            <button
                              onClick={() => toggleUserStatus(user, 'is_verified')}
                              className={`text-xs px-2 py-1 rounded ${
                                user.is_verified
                                  ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                  : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                              }`}
                              title={user.is_verified ? 'Verified' : 'Unverified'}
                            >
                              {user.is_verified ? <FaCheck /> : <FaTimes />}
                            </button>
                            <button
                              onClick={() => toggleUserStatus(user, 'stripe_transfers_enabled')}
                              className={`text-xs px-2 py-1 rounded ${
                                user.stripe_transfers_enabled
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                  : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                              }`}
                              title={user.stripe_transfers_enabled ? 'Payments enabled' : 'Payments disabled'}
                            >
                              <FaCreditCard />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowPasswordModal(true);
                            }}
                            className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 rounded transition"
                            title="Reset Password"
                          >
                            <FaKey />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowLogoModal(true);
                            }}
                            className="p-2 text-green-400 hover:text-green-300 hover:bg-green-500/20 rounded transition"
                            title="Upload Logo"
                          >
                            <FaImage />
                          </button>
                          <button
                            onClick={() => handleImpersonate(user)}
                            className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded transition"
                            title="Impersonate User"
                            disabled={user.is_superuser}
                          >
                            <FaSignInAlt />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition"
                            title="Delete User"
                            disabled={user.is_superuser}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronLeft />
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Password Reset Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md border border-gray-700">
              <h3 className="text-xl font-semibold mb-4">Reset Password</h3>
              <p className="text-gray-400 mb-4">
                Reset password for: <span className="text-white">{selectedUser?.email}</span>
              </p>
              <input
                type="password"
                placeholder="New password (min 8 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none mb-4"
              />
              <div className="flex space-x-3">
                <button
                  onClick={handleResetPassword}
                  disabled={newPassword.length < 8}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition"
                >
                  Reset Password
                </button>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setSelectedUser(null);
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md border border-gray-700">
              <h3 className="text-xl font-semibold mb-4 text-red-400">Delete User</h3>
              <p className="text-gray-400 mb-4">
                Are you sure you want to permanently delete user: <span className="text-white">{selectedUser?.email}</span>?
              </p>
              <p className="text-red-400 text-sm mb-4">
                This action cannot be undone and will delete all associated data.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteUser}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg transition"
                >
                  Delete User
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md border border-gray-700">
              <h3 className="text-xl font-semibold mb-4">Create New User</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email *</label>
                  <input
                    type="email"
                    placeholder="user@example.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Password * (min 8 characters)</label>
                  <input
                    type="password"
                    placeholder="Enter password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">First Name</label>
                    <input
                      type="text"
                      placeholder="John"
                      value={newUser.first_name}
                      onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Last Name</label>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={newUser.last_name}
                      onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Username</label>
                  <input
                    type="text"
                    placeholder="johndoe"
                    value={newUser.username}
                    onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleCreateUser}
                  disabled={!newUser.email || newUser.password.length < 8}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition"
                >
                  Create User
                </button>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUser({
                      email: '',
                      password: '',
                      first_name: '',
                      last_name: '',
                      username: ''
                    });
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logo Upload Modal */}
        {showLogoModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md border border-gray-700">
              <h3 className="text-xl font-semibold mb-4">Upload Logo</h3>
              <p className="text-gray-400 mb-4">
                Upload a logo for: <span className="text-white">{selectedUser?.email}</span>
              </p>
              {selectedUser?.logo_url && (
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-2">Current logo:</p>
                  <img
                    src={selectedUser.logo_url.startsWith('http') ? selectedUser.logo_url : `${config.API_URL}${selectedUser.logo_url}`}
                    alt="Current logo"
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                </div>
              )}
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Select image file</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg focus:border-blue-500 focus:outline-none text-gray-300"
                />
                <p className="text-xs text-gray-500 mt-1">Allowed: JPEG, PNG, GIF, WebP</p>
              </div>
              {selectedFile && (
                <div className="mb-4">
                  <p className="text-sm text-gray-400 mb-2">Preview:</p>
                  <img
                    src={URL.createObjectURL(selectedFile)}
                    alt="Preview"
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                </div>
              )}
              <div className="flex space-x-3">
                <button
                  onClick={handleLogoUpload}
                  disabled={!selectedFile || uploadingLogo}
                  className="flex-1 bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition"
                >
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                </button>
                <button
                  onClick={() => {
                    setShowLogoModal(false);
                    setSelectedFile(null);
                    setSelectedUser(null);
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserManagement;